import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import QuickOTPModal from '@/components/QuickOTPModal';
import { Building2 } from 'lucide-react';

/**
 * AuthPage — OTP-only entry point.
 * Renders the QuickOTPModal as a full-screen flow (no overlay backdrop).
 * On success, navigates to the appropriate dashboard.
 */
export default function AuthPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [done, setDone] = useState(false);

  // If already logged in, redirect immediately
  useEffect(() => {
    if (user) {
      redirectByRole(user.role, user.user_id);
    }
  }, [user]);

  // Honour `?return=<path>` for guest-gate post-auth bounce-back.
  // Falls back to role-based dashboard when no return param present.
  const getReturnUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const ret = params.get('return');
    // Only accept same-origin relative paths — never absolute URLs (open redirect protection).
    if (ret && ret.startsWith('/') && !ret.startsWith('//')) return ret;
    return null;
  };

  const redirectByRole = (role, userId) => {
    const overrideUrl = getReturnUrl();
    if (overrideUrl) { navigate(overrideUrl, { replace: true }); return; }
    if (role === 'admin') navigate('/admin', { replace: true });
    else if (role === 'institution') navigate(`/university/${user?.university_id}`, { replace: true });
    else if (role === 'agent') navigate(`/agents/${userId}`, { replace: true });
    else if (role === 'recruiter') navigate('/recruiter', { replace: true });
    else navigate('/colleges', { replace: true });
  };

  const handleSuccess = (userData) => {
    setDone(true);
    redirectByRole(userData.role, userData.user_id);
  };

  if (done) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center" data-testid="auth-page">
      <QuickOTPModal
        onSuccess={handleSuccess}
        onClose={() => navigate('/colleges')}
        guestTitle="Welcome back"
      />
      {/* Recruiter Entry Point — hidden Feb 14, 2026 per spec. Plan and
          dashboard still exist; only the signup CTA is suppressed.
          To re-enable: delete `RECRUITER_SIGNUP_HIDDEN = true` below. */}
      {false && (
      <div className="fixed bottom-6 left-0 right-0 flex justify-center z-50">
        <button onClick={() => navigate('/recruiter-signup')}
          className="px-5 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold shadow-lg shadow-orange-200 flex items-center gap-2 hover:shadow-xl transition-all"
          data-testid="recruiter-signup-link">
          <Building2 className="h-3.5 w-3.5" /> Hiring international talent? Sign up as Recruiter
        </button>
      </div>
      )}
    </div>
  );
}
